/**
 * Наказ про тривалу відсутність працівника (ч.1209): відпустка для догляду за
 * дитиною, військова служба, призупинення дії трудового договору. Проводок немає;
 * проведений наказ враховують табель, нарахування зарплати, середня зарплата,
 * дні відпустки, обʼєднана звітність і звіт про внесок.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ABSENCE_KIND_LABELS, CHILDCARE_AGE_LABELS, absenceOrders,
  type AbsenceKind, type ChildcareAgeGroup,
} from '@/api/absenceOrders';
import { employees } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import DatePopover from '@/components/DatePopover';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import { withCode } from '@/shared/utils/labels';

const DOC_TYPE = 'doc.absence_orders';

interface Form {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  absence_kind: AbsenceKind;
  childcare_age_group: ChildcareAgeGroup;
  start_date: string;
  end_date: string;
  basis: string;
  comment: string;
}

const today = new Date();
const EMPTY: Form = {
  organization_id: '', document_number: '',
  document_date: today.toISOString().slice(0, 16),
  employee_id: '', absence_kind: 'CHILDCARE', childcare_age_group: 'UNDER_3',
  start_date: today.toISOString().slice(0, 10), end_date: '', basis: '', comment: '',
};

/** Що зміниться в обліку після проведення — за видом відсутності. */
export const ABSENCE_EFFECTS: Record<AbsenceKind, string> = {
  CHILDCARE: 'Працівник лишається у штаті, але не входить у середньооблікову кількість. '
    + 'Табель за ці дні не заповнюється, зарплата і дні щорічної відпустки не нараховуються. '
    + 'Початок і закінчення відпустки потрапляють у додаток про трудові відносини обʼєднаної звітності.',
  MILITARY: 'Працівник лишається у штаті й у середньообліковій кількості, робоче місце зберігається. '
    + 'Табель за ці дні не заповнюється, зарплата і дні щорічної відпустки не нараховуються.',
  SUSPENDED: 'Працівник лишається у штаті й у середньообліковій кількості. '
    + 'Табель за ці дні не заповнюється, зарплата і дні щорічної відпустки не нараховуються.',
};

export default function AbsenceOrderEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [search] = useSearchParams();
  const isNew = !id || id === 'new';
  const empFromUrl = search.get('employee');
  const orgFromUrl = search.get('org');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs, hasPermissionInAnyOrg, totalAccessibleOrgs, loading: permsLoading } =
    usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['absence-order', id], queryFn: () => absenceOrders.get(id!), enabled: !isNew,
  });
  const [form, setForm] = useState<Form>(EMPTY);
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview(DOC_TYPE, form.organization_id, isNew);
  const v = useFormValidation();
  const [error, setError] = useState<string | null>(null);
  const [closeDate, setCloseDate] = useState('');
  const setF = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));

  const empListQ = useQuery({
    queryKey: ['employees-active', form.organization_id],
    queryFn: () => employees.list({ organization_id: form.organization_id, active_only: true, limit: 1000 }),
    enabled: !!form.organization_id,
  });
  const empCurQ = useQuery({
    queryKey: ['employee-full', form.employee_id],
    queryFn: () => employees.get(form.employee_id),
    enabled: !!form.employee_id,
  });

  // Новий документ: організація за замовчуванням (з адреси або перша доступна).
  useEffect(() => {
    if (!isNew) return;
    const firstOrg = (orgFromUrl && availableOrgs.some((o) => o.id === orgFromUrl) ? orgFromUrl : '')
      || availableOrgs[0]?.id || '';
    setForm((f) => f.organization_id || !firstOrg ? f : {
      ...f, organization_id: firstOrg, employee_id: empFromUrl ?? f.employee_id,
    });
  }, [isNew, availableOrgs, empFromUrl, orgFromUrl]);
  // Наявний документ: форма з сервера. Окремий ефект — залежить ЛИШЕ від даних документа
  // (спільний з переліком організацій ефект перезаписував би форму на кожну їх зміну).
  useEffect(() => {
    const d = docQuery.data;
    if (isNew || !d) return;
    setForm({
      organization_id: d.organization_id, document_number: d.document_number,
      document_date: d.document_date.slice(0, 16), employee_id: d.employee_id,
      absence_kind: d.absence_kind, childcare_age_group: d.childcare_age_group ?? 'UNDER_3',
      start_date: d.start_date, end_date: d.end_date ?? '',
      basis: d.basis ?? '', comment: d.comment ?? '',
    });
  }, [isNew, docQuery.data]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  function validate(): boolean {
    const ok = v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату наказу'],
      ['employee_id', form.employee_id, 'Оберіть співробітника'],
      ['start_date', form.start_date, 'Вкажіть перший день відсутності'],
    ]);
    if (ok && form.end_date && form.end_date < form.start_date) {
      setError('Дата закінчення не може бути раніше за дату початку');
      return false;
    }
    return ok;
  }
  const payload = () => ({
    document_number: form.document_number || undefined,
    document_date: new Date(form.document_date).toISOString(),
    employee_id: form.employee_id,
    absence_kind: form.absence_kind,
    childcare_age_group: form.absence_kind === 'CHILDCARE' ? form.childcare_age_group : null,
    start_date: form.start_date,
    end_date: form.end_date || null,
    basis: form.basis.trim() || null,
    comment: form.comment.trim() || null,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['absence-order', id] });
    qc.invalidateQueries({ queryKey: ['hr-orders-registry'] });
    qc.invalidateQueries({ queryKey: ['employee-full'] });
    qc.invalidateQueries({ queryKey: ['employees-full'] });
  };
  const onErr = (e: unknown) => setError(extractError(e));

  const create = useMutation({
    mutationFn: () => absenceOrders.create({ organization_id: form.organization_id, ...payload() }),
    onSuccess: (doc) => { setError(null); refresh(); navigate(`/absence-orders/${doc.id}`); },
    onError: onErr,
  });
  const update = useMutation({
    mutationFn: () => absenceOrders.update(id!, payload()),
    onSuccess: () => { setError(null); refresh(); },
    onError: onErr,
  });
  const unpost = useMutation({
    mutationFn: () => absenceOrders.unpost(id!),
    onSuccess: () => { setError(null); refresh(); }, onError: onErr,
  });
  const remove = useMutation({
    mutationFn: () => absenceOrders.remove(id!),
    onSuccess: () => { refresh(); navigate('/hr-orders'); }, onError: onErr,
  });
  // Завершити відсутність у проведеному наказі одним кроком: розпровести → дата → провести.
  const close = useMutation({
    mutationFn: async () => {
      await absenceOrders.unpost(id!);
      await absenceOrders.update(id!, { end_date: closeDate });
      await absenceOrders.post(id!);
    },
    onSuccess: () => { setError(null); setCloseDate(''); refresh(); },
    onError: (e) => { onErr(e); refresh(); },
  });

  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  async function onSaveAndPost() {
    if (!validate()) return;
    setError(null);
    try {
      const doc = isNew
        ? await absenceOrders.create({ organization_id: form.organization_id, ...payload() })
        : await absenceOrders.update(id!, payload());
      await absenceOrders.post(doc.id);
      refresh();
      if (isNew) navigate(`/absence-orders/${doc.id}`);
    } catch (e) {
      onErr(e);
      refresh();
    }
  }

  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{ padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid var(--badge-amber-bd)', color: 'var(--badge-amber-fg)', maxWidth: 600, margin: '1rem auto' }}>
            У вас немає прав створювати кадрові накази у жодній з ваших організацій ({totalAccessibleOrgs}).
            Зверніться до адміністратора акаунта.
          </div>
          <button onClick={() => navigate('/hr-orders')}>до списку</button>
        </div>
      </div>
    );
  }

  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [{
    label: 'картка працівника',
    title: form.employee_id ? 'Відкрити картку працівника' : 'Спершу оберіть співробітника',
    disabled: !form.employee_id,
    onClick: () => navigate(`/employees/${form.employee_id}`),
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'abs', title: 'Працівник і відсутність', required: 3,
      filled: (form.employee_id ? 1 : 0) + (form.absence_kind ? 1 : 0) + (form.start_date ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  const busy = create.isPending || update.isPending || close.isPending;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий наказ (тривала відсутність)' : `Наказ (тривала відсутність) № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            onBackToList={() => navigate('/hr-orders')}
            onSave={onSave}
            saveDisabled={busy}
            isPosted={isPosted}
            isNew={isNew}
            onPost={onSaveAndPost}
            postPending={busy}
            postTitle="Зберігає поточні зміни та проводить документ"
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити наказ?').then((ok) => { if (ok) remove.mutate(); }); }}
            deletePending={remove.isPending}
            entriesDoc={{ type: DOC_TYPE, id: isNew ? '' : id! }}
            railMode
            extraActions={railActions}
          />
        </div>
      </div>

      {v.banner()}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: DOC_TYPE, id: isNew ? '' : id! }} />
      }>
        <DocBlock index={1} title="Основні дані">
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Організація *</label>
              <select value={form.organization_id} disabled={!isNew}
                onChange={(e) => { setF({ organization_id: e.target.value, employee_id: '' }); v.clear('organization_id'); }}
                style={v.inputStyle('organization_id')}>
                <option value="">—</option>
                {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
                  <option key={o.id} value={o.id}>{withCode(o.name, o.code)}</option>
                ))}
              </select>
              {v.error('organization_id')}
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>№ наказу</label>
              <input value={form.document_number} disabled={isPosted}
                onChange={(e) => setF({ document_number: e.target.value })}
                placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''} />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Дата наказу *</label>
              <DatePopover withTime value={form.document_date} disabled={isPosted}
                onChange={(val) => { setF({ document_date: val }); v.clear('document_date'); }}
                style={v.inputStyle('document_date')} />
              {v.error('document_date')}
            </div>
          </div>
        </DocBlock>

        <DocBlock index={2} title="Працівник і відсутність">
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 8' }}>
              <label>Співробітник *</label>
              <select value={form.employee_id} disabled={!isNew && isPosted}
                onChange={(e) => { setF({ employee_id: e.target.value }); v.clear('employee_id'); }}
                style={v.inputStyle('employee_id')}>
                <option value="">— оберіть —</option>
                {empCurQ.data && !empListQ.data?.items.some((e) => e.id === empCurQ.data!.id) && (
                  <option value={empCurQ.data.id}>{withCode(empCurQ.data.full_name ?? '', empCurQ.data.code)}</option>
                )}
                {/* Лише штатні: цивільно-правовий договір відсутністю не оформлюється. */}
                {empListQ.data?.items.filter((e) => e.employment_type === 'STAFF').map((e) => (
                  <option key={e.id} value={e.id}>{withCode(e.full_name ?? '', e.code)}</option>
                ))}
              </select>
              {v.error('employee_id')}
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Вид відсутності *</label>
              <select value={form.absence_kind} disabled={isPosted}
                onChange={(e) => setF({ absence_kind: e.target.value as AbsenceKind })}>
                {(Object.keys(ABSENCE_KIND_LABELS) as AbsenceKind[]).map((k) => (
                  <option key={k} value={k}>{ABSENCE_KIND_LABELS[k]}</option>
                ))}
              </select>
            </div>
            {form.absence_kind === 'CHILDCARE' && (
              <div className="field" style={{ gridColumn: 'span 4' }}>
                <label>Вік дитини *</label>
                <select value={form.childcare_age_group} disabled={isPosted}
                  onChange={(e) => setF({ childcare_age_group: e.target.value as ChildcareAgeGroup })}>
                  {(Object.keys(CHILDCARE_AGE_LABELS) as ChildcareAgeGroup[]).map((k) => (
                    <option key={k} value={k}>{CHILDCARE_AGE_LABELS[k]}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Перший день відсутності *</label>
              <DatePopover value={form.start_date} disabled={isPosted}
                onChange={(val) => { setF({ start_date: val }); v.clear('start_date'); }}
                style={v.inputStyle('start_date')} />
              {v.error('start_date')}
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>Останній день відсутності</label>
              <DatePopover value={form.end_date} disabled={isPosted}
                onChange={(val) => setF({ end_date: val })} />
              <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                порожньо — відсутність триває
              </small>
            </div>
            <div className="field" style={{ gridColumn: 'span 12' }}>
              <label>Підстава</label>
              <input value={form.basis} disabled={isPosted} maxLength={500}
                placeholder="заява працівника, повістка, наказ про призупинення договору"
                onChange={(e) => setF({ basis: e.target.value })} />
            </div>
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
            {ABSENCE_EFFECTS[form.absence_kind]}
          </div>
          {isPosted && !docQuery.data?.end_date && (
            <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', border: '1px solid var(--border)',
              borderRadius: 8, background: 'var(--bg-soft)', display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}
              data-testid="absence-close">
              <div className="field" style={{ margin: 0, width: 200 }}>
                <label>Працівник повернувся — останній день відсутності</label>
                <DatePopover value={closeDate} onChange={setCloseDate} />
              </div>
              <button type="button" className="primary"
                disabled={!closeDate || closeDate < form.start_date || close.isPending}
                title={!closeDate ? 'Вкажіть останній день відсутності'
                  : closeDate < form.start_date ? 'Дата раніше за початок відсутності' : undefined}
                onClick={() => close.mutate()}>
                {close.isPending ? 'зберігаємо…' : 'завершити відсутність'}
              </button>
            </div>
          )}
        </DocBlock>

        <DocBlock index={3} title="Коментарі та вкладення">
          <div className="docblock__cols">
            <div className="field" style={{ margin: 0 }}>
              <label>Коментар</label>
              <input value={form.comment} disabled={isPosted}
                onChange={(e) => setF({ comment: e.target.value })} />
            </div>
            <div>
              <AttachmentsPanel organizationId={form.organization_id} documentType={DOC_TYPE} documentId={id} />
            </div>
          </div>
        </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: unknown } } }).response;
    const d = r?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x: { msg?: string }) => x?.msg ?? '').filter(Boolean).join('; ') || 'Помилка';
    return 'Помилка';
  }
  return String(e);
}
